import { CountryCode } from 'libphonenumber-js';
import {
  array,
  boolean,
  custom,
  discriminatedUnion,
  email,
  httpUrl,
  type input,
  literal,
  number,
  object,
  type output,
  string,
} from 'zod';
import { isValidPhoneNumber } from '../../shared/functions';
import { Country } from '../country';
import { Nomination } from '../nomination';
import { Organization } from '../organization';
import { associatedCountriesRequestSchema } from './associated-countries-request.schema';
import {
  additionalContactValidationSchema,
  countryValidationSchema,
  jointNomineeValidationSchema,
  nominationDocumentValidationSchema,
  nominationSubmissionContentValidationSchema,
  organizationValidationSchema,
  ownerValidationSchema,
} from './nomination-validation.schema';

const nominationValidationInputSchema = object({
  nomination: custom<Nomination>(),
  organization: custom<Organization>(),
  editingAssociatedCountries: array(custom<Country>()),
  otherPartnerTypeId: number().optional(),
  isCountryAward: boolean(),
  organizationPhoneCountryCode: custom<CountryCode>().optional(),
  requiresWrittenNomination: boolean(),
  writtenNominationContent: string(),
  hasExecutiveSummaryDocument: boolean(),
});

const nominationWizardValidationInputSchema = nominationValidationInputSchema.extend({
  isCeoEmailIncluded: boolean(),
  jointNomineeCeoEmailIncluded: array(boolean()),
});

export type NominationValidationInput = input<typeof nominationValidationInputSchema>;
export type NominationWizardValidationInput = input<typeof nominationWizardValidationInputSchema>;

type OwnerValidationInput = input<typeof ownerValidationSchema>;

export const yourInformationValidationSchema = nominationValidationInputSchema
  .transform((input): OwnerValidationInput => input.nomination.owner)
  .pipe(ownerValidationSchema);

const countryAwardAssociationValidationSchema = discriminatedUnion('isCountryAward', [
  object({
    isCountryAward: literal(false),
    editingAssociatedCountries: array(countryValidationSchema),
  }),
  object({
    isCountryAward: literal(true),
    editingAssociatedCountries: associatedCountriesRequestSchema.shape.associatedCountries,
  }),
]);

const companyContextValidationSchema = object({
  marketplaceOfferUrl: httpUrl({
    error: 'Enter a valid Marketplace offer URL.',
  }).optional(),
  otherPartnerTypeId: number().optional(),
  organizationPhoneCountryCode: custom<CountryCode>().optional(),
});

const organizationPhoneValidationSchema = object({
  phoneNumber: string(),
  organizationPhoneCountryCode: custom<CountryCode>().optional(),
})
  .refine(
    input =>
      input.phoneNumber.length === 0 ||
      isValidPhoneNumber(
        input.phoneNumber,
        input.organizationPhoneCountryCode,
      ),
    {
      error: 'Enter a valid phone number.',
      path: ['phoneNumber'],
    },
  );

const partnerTypesValidationSchema = array(object({
  partnerTypeId: number(),
  description: string(),
}));

const partnerTypeValidationSchema = object({
  partnerTypes: partnerTypesValidationSchema,
})
  .refine(input => input.partnerTypes.length > 0, {
    error: 'Select at least one partner type.',
    path: ['partnerTypes'],
  });

const otherPartnerTypeValidationSchema = object({
  partnerTypes: partnerTypesValidationSchema,
  otherPartnerTypeId: number().optional(),
})
  .refine(
    input =>
      input.otherPartnerTypeId === undefined ||
      input.partnerTypes.every(
        partnerType =>
          partnerType.partnerTypeId !== input.otherPartnerTypeId ||
          partnerType.description.length >= 5,
      ),
    {
      error: 'Enter at least five characters for the Other partner type.',
      path: ['partnerTypes'],
    },
  );

const companyInformationFieldsValidationSchema = organizationValidationSchema
  .and(companyContextValidationSchema)
  .and(countryAwardAssociationValidationSchema)
  .and(organizationPhoneValidationSchema)
  .and(partnerTypeValidationSchema)
  .and(otherPartnerTypeValidationSchema);

const requiredCeoEmailValidationSchema = string()
  .optional()
  .transform(regionalCeoEmail => regionalCeoEmail || '')
  .pipe(string()
    .min(1, { error: 'Enter the email address.', abort: true })
    .pipe(email({ error: 'Enter a valid email address.' })));

const ceoEmailValidationSchema = discriminatedUnion('isCeoEmailIncluded', [
  object({
    isCeoEmailIncluded: literal(false),
    regionalCeoEmail: string().optional(),
  }),
  object({
    isCeoEmailIncluded: literal(true),
    regionalCeoEmail: requiredCeoEmailValidationSchema,
  }),
]);

const nominationWizardCompanyInformationFieldsValidationSchema =
  companyInformationFieldsValidationSchema.and(ceoEmailValidationSchema);

export const companyInformationValidationSchema = nominationValidationInputSchema
  .transform((input) => {
    const validationInput: input<typeof companyInformationFieldsValidationSchema> = {
      mpnId: input.organization.mpnId,
      companyName: input.organization.companyName,
      address1: input.organization.address1,
      address2: input.organization.address2,
      city: input.organization.city,
      stateProvince: input.organization.stateProvince,
      postalCode: input.organization.postalCode,
      country: input.organization.country,
      phoneNumber: input.organization.phoneNumber,
      websiteUrl: input.organization.websiteUrl,
      partnerTypes: input.nomination.partnerTypes,
      editingAssociatedCountries: input.editingAssociatedCountries,
      isCountryAward: input.isCountryAward,
    };

    if (input.nomination.marketplaceOfferUrl) {
      validationInput.marketplaceOfferUrl = input.nomination.marketplaceOfferUrl;
    }

    if (input.organization.regionalCeoFullName) {
      validationInput.regionalCeoFullName = input.organization.regionalCeoFullName;
    }

    if (input.otherPartnerTypeId !== undefined) {
      validationInput.otherPartnerTypeId = input.otherPartnerTypeId;
    }
    
    if (input.organizationPhoneCountryCode !== undefined) {
      validationInput.organizationPhoneCountryCode = input.organizationPhoneCountryCode;
    }

    return validationInput;
  })
  .pipe(companyInformationFieldsValidationSchema);

export const nominationWizardCompanyInformationValidationSchema = nominationWizardValidationInputSchema
  .transform((input) => {
    const validationInput: input<typeof nominationWizardCompanyInformationFieldsValidationSchema> = {
      mpnId: input.organization.mpnId,
      companyName: input.organization.companyName,
      address1: input.organization.address1,
      address2: input.organization.address2,
      city: input.organization.city,
      stateProvince: input.organization.stateProvince,
      postalCode: input.organization.postalCode,
      country: input.organization.country,
      phoneNumber: input.organization.phoneNumber,
      websiteUrl: input.organization.websiteUrl,
      regionalCeoEmail: input.organization.regionalCeoEmail,
      isCeoEmailIncluded: input.isCeoEmailIncluded,
      partnerTypes: input.nomination.partnerTypes,
      editingAssociatedCountries: input.editingAssociatedCountries,
      isCountryAward: input.isCountryAward,
    };

    if (input.nomination.marketplaceOfferUrl) {
      validationInput.marketplaceOfferUrl = input.nomination.marketplaceOfferUrl;
    }

    if (input.organization.regionalCeoFullName) {
      validationInput.regionalCeoFullName = input.organization.regionalCeoFullName;
    }

    if (input.otherPartnerTypeId !== undefined) {
      validationInput.otherPartnerTypeId = input.otherPartnerTypeId;
    }

    if (input.organizationPhoneCountryCode !== undefined) {
      validationInput.organizationPhoneCountryCode = input.organizationPhoneCountryCode;
    }

    return validationInput;
  })
  .pipe(nominationWizardCompanyInformationFieldsValidationSchema);

export const additionalContactsValidationSchema = nominationValidationInputSchema
  .transform(input => input.nomination.contacts.map(contact => ({
    prefix: contact.prefix,
    firstName: contact.firstName,
    lastName: contact.lastName,
    emailAddress: contact.emailAddress,
    contactNotificationTypeId: contact.contactNotificationTypeId,
  })))
  .pipe(array(additionalContactValidationSchema));

const nominationWizardJointNomineeFieldsValidationSchema =
  jointNomineeValidationSchema.and(ceoEmailValidationSchema);

export const nominationWizardJointNomineeValidationSchema = object({
  jointNominee: custom<Organization>(),
  isCeoEmailIncluded: boolean(),
})
  .transform((input): input<typeof nominationWizardJointNomineeFieldsValidationSchema> => {
    const validationInput: input<typeof nominationWizardJointNomineeFieldsValidationSchema> = {
      mpnId: input.jointNominee.mpnId,
      companyName: input.jointNominee.companyName,
      address1: input.jointNominee.address1,
      address2: input.jointNominee.address2,
      city: input.jointNominee.city,
      stateProvince: input.jointNominee.stateProvince,
      postalCode: input.jointNominee.postalCode,
      country: input.jointNominee.country,
      phoneNumber: input.jointNominee.phoneNumber,
      websiteUrl: input.jointNominee.websiteUrl,
      prefix: input.jointNominee.prefix,
      firstName: input.jointNominee.firstName,
      lastName: input.jointNominee.lastName,
      emailAddress: input.jointNominee.emailAddress,
      isCeoEmailIncluded: input.isCeoEmailIncluded,
    };

    if (input.jointNominee.regionalCeoEmail) {
      validationInput.regionalCeoEmail = input.jointNominee.regionalCeoEmail;
    }

    if (input.jointNominee.regionalCeoFullName) {
      validationInput.regionalCeoFullName = input.jointNominee.regionalCeoFullName;
    }

    return validationInput;
  })
  .pipe(nominationWizardJointNomineeFieldsValidationSchema);

export const jointNomineesValidationSchema = nominationWizardValidationInputSchema
  .transform((input): input<typeof nominationWizardJointNomineeValidationSchema>[] =>
    input.nomination.jointNominees.map((jointNominee, jointNomineeIndex) => ({
      jointNominee,
      isCeoEmailIncluded: input.jointNomineeCeoEmailIncluded[jointNomineeIndex],
    })))
  .pipe(array(nominationWizardJointNomineeValidationSchema));

const nominationSubmissionFieldsValidationSchema =
  nominationSubmissionContentValidationSchema.and(nominationDocumentValidationSchema);

export const nominationSubmissionValidationSchema = nominationValidationInputSchema
  .transform(input => ({
    name: input.nomination.name,
    abstract: input.nomination.abstract,
    customerReferences: input.nomination.customerReferences.map(customerReference => ({
      prefix: customerReference.prefix,
      firstName: customerReference.firstName,
      lastName: customerReference.lastName,
      title: customerReference.title,
      emailAddress: customerReference.emailAddress,
      phoneNumber: customerReference.phoneNumber,
      companyName: customerReference.companyName,
      description: customerReference.description,
    })),
    websites: input.nomination.websites.map(website => ({
      websiteURL: website.websiteURL,
    })),
    requiresWrittenNomination: input.requiresWrittenNomination,
    writtenNominationContent: input.writtenNominationContent,
    hasExecutiveSummaryDocument: input.hasExecutiveSummaryDocument,
  }))
  .pipe(nominationSubmissionFieldsValidationSchema);

export type YourInformationValidation = output<typeof yourInformationValidationSchema>;
export type CompanyInformationValidation = output<typeof companyInformationValidationSchema>;
export type NominationWizardCompanyInformationValidation = output<typeof nominationWizardCompanyInformationValidationSchema>;
export type NominationWizardJointNomineeValidation = output<typeof nominationWizardJointNomineeValidationSchema>;
export type AdditionalContactsValidation = output<typeof additionalContactsValidationSchema>;
export type JointNomineesValidation = output<typeof jointNomineesValidationSchema>;
export type NominationSubmissionValidation = output<typeof nominationSubmissionValidationSchema>;
